/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Badge,
	Button,
	CloseButton,
	Flex,
	Textarea,
	Tooltip,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTerminationPartnerCompanyStore } from '../../../../../components/ManageAgentPartner/Miscellaneous/IntermediaryStore';
import c from '../../../../../constant';

const M_STATUS = gql`
	mutation up($id: String!, $approvalStatus: Boolean, $rejectionReason: String) {
		submitTerminationPartnerItemApprovalStatus(id: $id, approvalStatus: $approvalStatus, rejectionReason: $rejectionReason) {
			id
			requestId
			partner {
				id
				name
				nameKhmer
				type
				logo
			}
			partnerType
			model
			approvalStatus
			rejectionReason
			letterOfNotification
			letterOfTerminationApproval
			letterOfBoardOfDirectors
		}
	}
`;

function UpdateTerminationPartnerItemApprovalStatus({ id = null, approvalStatus = null, onCallback = () => null }) {
	const cancelRef = useRef();
	const toast = useToast();
	const { t } = useTranslation();
	const { isPermitted } = useTerminationPartnerCompanyStore((state) => state.isPermitted);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [rejectionReason, setRejectionReason] = useState('');
	const [updateStatus, { error, loading, data }] = useMutation(M_STATUS);

	useEffect(() => {
		if (data?.submitTerminationPartnerItemApprovalStatus) {
			onCallback(data?.submitTerminationPartnerItemApprovalStatus);
			onClose();
		}
	}, [data]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function approve() {
		updateStatus({
			variables: {
				id,
				approvalStatus: true,
				rejectionReason: null,
			},
		});
	}

	function reject() {
		updateStatus({
			variables: {
				id,
				approvalStatus: false,
				rejectionReason: rejectionReason,
			},
		});
	}

	function reset() {
		updateStatus({
			variables: {
				id,
				approvalStatus: null,
				rejectionReason: null,
			},
		});
	}

	return (
		<>
			{approvalStatus === null && (
				<>
					<Flex justifyContent="flex-start" alignItems="center">
						<Tooltip label={t('approve')}>
							<Button
								borderRadius={c.borderRadius}
								size="sm"
								isLoading={loading}
								isDisabled={isPermitted ? false : true}
								colorScheme="green"
								onMouseDown={approve}
								ml={0}
							>
								{t('approve')}
							</Button>
						</Tooltip>
						<Tooltip label={t('resubmit')}>
							<Button
								borderRadius={c.borderRadius}
								size="sm"
								isLoading={loading}
								isDisabled={isPermitted ? false : true}
								colorScheme="red"
								onMouseDown={onOpen}
								ml={2}
							>
								{t('resubmit')}
							</Button>
						</Tooltip>
					</Flex>
				</>
			)}
			{approvalStatus === true && (
				<>
					<Flex justifyContent="flex-start" alignItems="center">
						<Tooltip label={t('approved')}>
							<Badge colorScheme="green" variant="solid">
								{t('approved')}
							</Badge>
						</Tooltip>
						<Tooltip label={t('You can change this status')}>
							<CloseButton
								borderRadius={c.borderRadius}
								variant="solid"
								hidden={isPermitted ? false : true}
								onMouseDown={reset}
								size="xs"
								p={1}
								ml={1}
								color="gray.400"
							/>
						</Tooltip>
					</Flex>
				</>
			)}
			{approvalStatus === false && (
				<>
					<Flex justifyContent="flex-start" alignItems="center">
						<Tooltip label={t('resubmit')}>
							<Badge colorScheme="red" variant="solid">
								{t('resubmit')}
							</Badge>
						</Tooltip>
						<Tooltip label={t('You can change this status')}>
							<CloseButton
								borderRadius={c.borderRadius}
								variant="solid"
								hidden={isPermitted ? false : true}
								onMouseDown={reset}
								size="xs"
								p={1}
								ml={1}
								color="gray.400"
							/>
						</Tooltip>
					</Flex>
				</>
			)}
			<AlertDialog closeOnEsc={false} closeOnOverlayClick={false} isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Reason of resubmission')}
						</AlertDialogHeader>
						<AlertDialogBody>
							{t('Please input reason of resubmission')}
							<Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.currentTarget.value)} />
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button borderRadius={c.borderRadius} isLoading={loading} colorScheme="gray" ref={cancelRef} onMouseDown={onClose} ml={0}>
								{t('Cancel')}
							</Button>
							<Button
								borderRadius={c.borderRadius}
								isLoading={loading}
								isDisabled={rejectionReason ? false : true}
								colorScheme="red"
								onMouseDown={reject}
								ml={2}
							>
								{t('Resubmit')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}

export default UpdateTerminationPartnerItemApprovalStatus;
